import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Seo from "@/components/feature/Seo";
import MediaCard from "@/components/feature/MediaCard";
import FilterPanel from "./components/FilterPanel";
import { mediaItems } from "@/mocks/media";
import { newsItems } from "@/mocks/news";
import {
  getAllRegions,
  getAllYears,
} from "@/lib/catalog";
import type { MediaItem } from "@/types/media";

const CATEGORY_TYPES = ["电视剧", "电影", "资讯"] as const;
type CategoryType = (typeof CATEGORY_TYPES)[number];

const introMap: Record<CategoryType, string> = {
  电视剧: "挑一部好剧，追一个故事。这里汇集了唐心精选的热播剧集与高分口碑剧，附评分与剧情简介，帮你快速锁定值得追的下一部。",
  电影: "从院线新片到经典佳作，唐心为你整理值得一看的电影作品，涵盖剧情、科幻、文艺等多个类型，附评分与作品亮点。",
  资讯: "第一时间掌握影视行业动态与热点话题，从票房盘点、新片定档到幕后专访与前瞻解读，一站式了解影视圈大小事。",
};

const PAGE_SIZE = 8;

export default function CategoryPage() {
  const { type: rawType } = useParams();
  const navigate = useNavigate();
  const type = (CATEGORY_TYPES as readonly string[]).includes(rawType ?? "")
    ? (rawType as CategoryType)
    : null;

  const [region, setRegion] = useState("all");
  const [year, setYear] = useState("all");
  const [rating, setRating] = useState("all");
  const [tag, setTag] = useState("all");
  const [sort, setSort] = useState<"latest" | "hot" | "rating">("latest");
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const regions = useMemo(() => getAllRegions(), []);
  const years = useMemo(() => getAllYears(), []);
  const tags = useMemo(
    () => Array.from(new Set(newsItems.map((n) => n.tag))),
    []
  );

  const isMediaType = type === "电视剧" || type === "电影";

  const mediaResult = useMemo(() => {
    if (!isMediaType) return [] as MediaItem[];
    let list = mediaItems.filter((m) => m.type === type);
    if (region !== "all") list = list.filter((m) => m.region === region);
    if (year !== "all") list = list.filter((m) => m.year === Number(year));
    if (rating !== "all") {
      list = list.filter((m) => {
        if (rating === "9") return m.rating >= 9;
        if (rating === "8.5") return m.rating >= 8.5;
        if (rating === "8") return m.rating >= 8;
        if (rating === "below8") return m.rating < 8;
        return true;
      });
    }
    if (sort === "hot") list = [...list].sort((a, b) => b.hot - a.hot);
    else if (sort === "rating")
      list = [...list].sort((a, b) => b.rating - a.rating);
    else list = [...list].sort((a, b) => b.year - a.year);
    return list;
  }, [isMediaType, type, region, year, rating, sort]);

  const newsResult = useMemo(() => {
    if (type !== "资讯") return [];
    let list = [...newsItems];
    if (tag !== "all") list = list.filter((n) => n.tag === tag);
    list.sort((a, b) => (a.date < b.date ? 1 : -1));
    return list;
  }, [type, tag]);

  const totalCount = isMediaType ? mediaResult.length : newsResult.length;
  const hasActiveFilter =
    region !== "all" ||
    year !== "all" ||
    rating !== "all" ||
    tag !== "all";

  const resetAll = () => {
    setRegion("all");
    setYear("all");
    setRating("all");
    setTag("all");
  };

  const categoryGroup = {
    key: "category",
    label: "类别",
    value: type ?? "",
    options: CATEGORY_TYPES.map((t) => ({ label: t, value: t })),
    onSelect: (value: string) => {
      resetAll();
      setVisible(PAGE_SIZE);
      navigate(`/category/${value}`);
    },
  };

  const filterGroups = [
    categoryGroup,
    ...(isMediaType
      ? [
          {
            key: "region",
            label: "地区",
            value: region,
            options: [
              { label: "全部", value: "all" },
              ...regions.map((r) => ({ label: r, value: r })),
            ],
            onSelect: (v: string) => {
              setRegion(v);
              setVisible(PAGE_SIZE);
            },
          },
          {
            key: "year",
            label: "年份",
            value: year,
            options: [
              { label: "全部", value: "all" },
              ...years.map((y) => ({ label: `${y}`, value: `${y}` })),
            ],
            onSelect: (v: string) => {
              setYear(v);
              setVisible(PAGE_SIZE);
            },
          },
          {
            key: "rating",
            label: "评分区间",
            value: rating,
            options: [
              { label: "全部", value: "all" },
              { label: "9.0 以上", value: "9" },
              { label: "8.5 以上", value: "8.5" },
              { label: "8.0 以上", value: "8" },
              { label: "8.0 以下", value: "below8" },
            ],
            onSelect: (v: string) => {
              setRating(v);
              setVisible(PAGE_SIZE);
            },
          },
        ]
      : [
          {
            key: "tag",
            label: "资讯分类",
            value: tag,
            options: [
              { label: "全部", value: "all" },
              ...tags.map((t) => ({ label: t, value: t })),
            ],
            onSelect: (v: string) => {
              setTag(v);
              setVisible(PAGE_SIZE);
            },
          },
        ]),
  ];

  const sortOptions: { label: string; value: "latest" | "hot" | "rating" }[] = [
    { label: "最新", value: "latest" },
    { label: "最热", value: "hot" },
    { label: "评分", value: "rating" },
  ];

  const categoryTitle = type ? `${type} - 唐心影视库` : "分类 - 唐心影视库";
  const categoryDesc = type
    ? `浏览唐心精选的最新${type}影视作品，获取评分和剧情介绍。`
    : "浏览唐心影视作品库。";

  const visibleMedia = mediaResult.slice(0, visible);
  const visibleNews = newsResult.slice(0, visible);

  return (
    <SiteLayout>
      <Seo
        title={categoryTitle}
        description={categoryDesc}
        keywords={type ? `唐心,${type},影视推荐,剧情介绍` : "唐心,影视推荐"}
        canonicalPath={type ? `/category/${type}` : "/category"}
      />

      <div className="w-full px-4 md:px-6 pt-6">
        <nav className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-400">
          <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">
            首页
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-600">{type ?? "分类"}</span>
        </nav>

        <div className="mt-4 flex flex-col gap-3 border-b border-background-200 pb-5 md:flex-row md:items-end md:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground-950 font-heading md:text-3xl">
              {type ? `${type}库` : "影视分类"}
            </h1>
            <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-500">
              {type ? introMap[type] : "请选择分类查看内容。"}
            </p>
          </div>
          <span className="shrink-0 rounded-md bg-secondary-100 px-3 py-1.5 text-xs text-secondary-900">
            共 {totalCount} 条内容
          </span>
        </div>
      </div>

      <div className="w-full px-4 md:px-6 mt-6">
        <div className="lg:flex lg:gap-8">
          <aside className="hidden lg:block lg:w-60 shrink-0">
            <div className="sticky top-24 rounded-lg bg-background-100 p-5 ring-1 ring-background-200">
              <FilterPanel
                title="筛选条件"
                groups={filterGroups}
                onReset={resetAll}
              />
            </div>
          </aside>

          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => setDrawerOpen(true)}
                className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-background-100 px-3.5 py-2 text-sm font-medium text-foreground-700 ring-1 ring-background-200 hover:bg-background-200 transition-colors cursor-pointer lg:hidden"
              >
                <i className="ri-filter-3-line"></i>
                筛选
                {hasActiveFilter ? (
                  <span className="h-1.5 w-1.5 rounded-full bg-accent-500"></span>
                ) : null}
              </button>

              <div className="ml-auto flex items-center gap-1 rounded-full bg-background-100 p-1 ring-1 ring-background-200">
                {sortOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      setSort(opt.value);
                      setVisible(PAGE_SIZE);
                    }}
                    className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors cursor-pointer ${
                      sort === opt.value
                        ? "bg-primary-600 text-background-50"
                        : "text-foreground-600 hover:text-primary-600"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {totalCount === 0 ? (
              <div className="mt-10 flex flex-col items-center justify-center rounded-lg bg-background-100 py-16 ring-1 ring-background-200">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-3xl text-foreground-400">
                  <i className="ri-movie-2-line"></i>
                </span>
                <p className="mt-4 text-base font-medium text-foreground-800">
                  没有找到匹配的内容
                </p>
                <p className="mt-1 text-sm text-foreground-500">
                  试着调整或清空筛选条件后再看看
                </p>
                <button
                  type="button"
                  onClick={resetAll}
                  className="mt-5 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-700 transition-colors cursor-pointer"
                >
                  清空筛选
                </button>
              </div>
            ) : isMediaType ? (
              <>
                <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                  {visibleMedia.map((item, index) => (
                    <MediaCard key={item.id} item={item} index={index} />
                  ))}
                </div>
                {visible < mediaResult.length ? (
                  <div className="mt-8 flex justify-center">
                    <button
                      type="button"
                      onClick={() => setVisible((v) => v + PAGE_SIZE)}
                      className="whitespace-nowrap rounded-md bg-primary-600 px-6 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-700 transition-colors cursor-pointer"
                    >
                      加载更多（{mediaResult.length - visible}）
                    </button>
                  </div>
                ) : null}
              </>
            ) : (
              <>
                <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {visibleNews.map((item, index) => (
                    <Link
                      key={item.id}
                      to={`/detail/${item.id}`}
                      className="group block cursor-pointer overflow-hidden rounded-lg bg-background-100 ring-1 ring-background-200 animate-fade-up"
                      style={{ animationDelay: `${Math.min(index, 8) * 60}ms` }}
                    >
                      <div className="relative w-full aspect-[16/9] overflow-hidden">
                        <img
                          src={item.cover}
                          alt={item.title}
                          title={`${item.title} 资讯`}
                          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        />
                        <span className="absolute left-3 top-3 rounded-md bg-accent-500 px-2 py-0.5 text-xs font-semibold text-foreground-950">
                          {item.tag}
                        </span>
                      </div>
                      <div className="p-4">
                        <h3 className="text-sm font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2">
                          {item.title}
                        </h3>
                        <p className="mt-2 text-xs leading-relaxed text-foreground-500 line-clamp-2">
                          {item.summary}
                        </p>
                        <div className="mt-3 flex items-center gap-3 text-xs text-foreground-400">
                          <span>{item.date}</span>
                          <span>{item.source}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
                {visible < newsResult.length ? (
                  <div className="mt-8 flex justify-center">
                    <button
                      type="button"
                      onClick={() => setVisible((v) => v + PAGE_SIZE)}
                      className="whitespace-nowrap rounded-md bg-primary-600 px-6 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-700 transition-colors cursor-pointer"
                    >
                      加载更多（{newsResult.length - visible}）
                    </button>
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>
      </div>

      {drawerOpen ? (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setDrawerOpen(false)}
          ></div>
          <div className="absolute inset-y-0 left-0 w-80 max-w-[85%] overflow-y-auto bg-background-50 p-5 animate-fade-in">
            <FilterPanel
              title="筛选条件"
              groups={filterGroups}
              onReset={resetAll}
              footer={
                <button
                  type="button"
                  onClick={() => setDrawerOpen(false)}
                  className="mt-2 w-full whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-700 transition-colors cursor-pointer"
                >
                  查看 {totalCount} 条结果
                </button>
              }
            />
          </div>
        </div>
      ) : null}
    </SiteLayout>
  );
}